import React, { useEffect, useRef } from 'react';
import {
  ArticleForm,
  EventForm,
  CanticleForm,
  BookForm,
} from '../';
import { AlbumEditor } from './AlbumEditor';
import { apiService } from '../../services';
import {
  Article,
  Event,
  Album,
  Book,
  Canticle,
  ArticleCreate,
  EventCreate,
  AlbumCreate,
  BookCreate,
  CanticleCreate,
} from '../../types';

type SelectedType = 'articles' | 'events' | 'albums' | 'canticles' | 'books';

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: SelectedType;
  item: Article | Event | Album | Book | Canticle;
  loading?: boolean;
  error?: string | null;
  onUpdate: (
    id: number,
    type: SelectedType,
    data: Partial<ArticleCreate | EventCreate | AlbumCreate | BookCreate | CanticleCreate>,
    files?: File | FileList
  ) => Promise<Article | Event | Album | Book | Canticle | void>;
  onAlbumUpdate?: (data: Partial<AlbumCreate>) => Promise<void>;
  onAlbumAddMedia?: (files: FileList) => Promise<void>;
  onAlbumDeleteMedia?: (mediaId: number) => Promise<void>;
  albumMediaItems?: any[];
  albumLoading?: boolean;
}

const getTypeLabel = (type: SelectedType): string => {
  const labels: Record<SelectedType, string> = {
    articles: 'Article',
    events: 'Événement',
    albums: 'Album',
    canticles: 'Cantique',
    books: 'Édition',
  };
  return labels[type];
};

export const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose,
  type,
  item,
  loading = false,
  error = null,
  onUpdate,
  onAlbumUpdate,
  onAlbumAddMedia,
  onAlbumDeleteMedia,
  albumMediaItems = [],
  albumLoading = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && e.target === modalRef.current) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const commonProps = {
    loading,
    error,
    onCancel: onClose,
  };

  const renderForm = () => {
    if (type === 'albums') {
      if (!onAlbumAddMedia || !onAlbumDeleteMedia) {
        return <div>Erreur: Fonctions de gestion des médias manquantes</div>;
      }
      return (
        <AlbumEditor
          albumId={item.id}
          initialData={item as Album}
          loading={albumLoading}
          error={error}
          mediaItems={albumMediaItems}
          onUpdate={async (data) => {
            if (onAlbumUpdate) {
              await onAlbumUpdate(data);
              onClose();
            }
          }}
          onAddMedia={onAlbumAddMedia}
          onDeleteMedia={onAlbumDeleteMedia}
        />
      );
    }

    switch (type) {
      case 'articles':
        return (
          <ArticleForm
            onSubmit={async (data, imageFile) => {
              const updatedArticle = await onUpdate(item.id, 'articles', data, imageFile);
              // Return the updated article from the handler, or fallback to item if handler doesn't return it
              return (updatedArticle as Article) || item as Article;
            }}
            initialData={item as Article}
            onFileUpload={async (articleId, files) => {
              for (const file of files) {
                await apiService.uploadArticleFile(articleId, file);
              }
            }}
            onFileDelete={async (articleId, fileIndex) => {
              await apiService.deleteArticleFile(articleId, fileIndex);
            }}
            {...commonProps}
          />
        );
      case 'events':
        return (
          <EventForm
            onSubmit={async (data, imageFile) => {
              await onUpdate(item.id, 'events', data, imageFile);
              onClose();
            }}
            initialData={item as Event}
            {...commonProps}
          />
        );
      case 'canticles':
        return (
          <CanticleForm
            onSubmit={async (data, pdfFile) => {
              await onUpdate(item.id, 'canticles', data, pdfFile);
              onClose();
            }}
            initialData={item as Canticle}
            {...commonProps}
          />
        );
      case 'books':
        return (
          <BookForm
            onSubmit={async (data, coverFile) => {
              await onUpdate(item.id, 'books', data, coverFile);
              onClose();
            }}
            initialData={item as Book}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  const getTitle = () => {
    if ('title' in item) return item.title;
    if ('name' in item) return item.name;
    return `Élément #${(item as { id: number }).id}`;
  };

  return (
    <div
      ref={modalRef}
      className='fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm'
      role='dialog'
      aria-modal='true'
      aria-labelledby='edit-modal-title'
    >
      <div className='relative w-full max-w-2xl bg-white rounded-lg shadow-xl p-6 m-4 max-h-[90vh] overflow-y-auto'>
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded'
          aria-label='Fermer'
        >
          <svg
            className='w-6 h-6'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            aria-hidden='true'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>

        <h2
          id='edit-modal-title'
          className='text-2xl font-bold text-gray-900 mb-6'
        >
          Modifier {getTypeLabel(type)}: {getTitle()}
        </h2>

        {renderForm()}
      </div>
    </div>
  );
};
